import { PurchaserListFilters, PurchaserStatus, PurchaserStatuses } from '@buyerflo/core/schemas/purchaser'
import { SavedFilterSchema } from '@buyerflo/core/schemas/savedFilter'
import { Card } from '@buyerflo/ui/components/Card'
import { StatusBadge } from '@buyerflo/ui/components/StatusBadge'
import { useMutation } from '@tanstack/react-query'
import { usePurchaserConfigApi, usePurchasersApi, useSavedFiltersApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import { MultipleAgentInput } from 'components/agentInput'
import { Autocomplete } from 'components/autocomplete'
import { ExternalLink } from '@tamagui/lucide-icons-2'
import { AddRounded, ClearRounded, CloseRounded, FilterAltRounded } from 'components/icons'
import { Label } from 'components/label'
import Loading from 'components/Loading'
import SectionHead from 'components/sectionHead'
import { MobileFriendlyStack } from 'components/stack'
import { useOrgId } from 'helpers'
import { useSearchParamState, useSessionStorageState } from 'helperFunctions/react'
import { omit, prop } from 'ramda'
import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import Button from 'components/button'
import { Dialog, Input, Text, View, XStack, YStack } from 'tamagui'
import { useDebounce } from 'use-debounce'
import { useTRPC } from 'utils/trpc'
import PurchasersTableDesktop from './purchasersTableDesktop'
import PurchasersTableMobile from './purchasersTableMobile'

interface ActionFilterModalProps {
    close: () => void
    actionFilters: Record<string, string[]>
    setActionFilters: (filter: Record<string, string[]>) => void
}

const ActionFilterModal = ({ close, ...props }: ActionFilterModalProps) => {
    const purchaserConfigApi = usePurchaserConfigApi()

    const [actionFilter, setActionFilter] = useState(props.actionFilters)

    return (
        <Dialog.Content
            bordered
            elevate
            padding="$4"
            borderRadius="$4"
        >
            <Dialog.Close asChild>
                <Button
                    size="$3"
                    circular
                    position="absolute"
                    top="$2"
                    right="$2"
                    onPress={close}
                >
                    <CloseRounded />
                </Button>
            </Dialog.Close>
            <Dialog.Title>Action Filters</Dialog.Title>
            <Dialog.Description>
                {purchaserConfigApi.data ? (
                    <YStack gap="$4">
                        {purchaserConfigApi.data.map(config => (
                            <Autocomplete
                                key={config.id}
                                multiple
                                options={config.configValues.map(v => v.value)}
                                value={actionFilter[config.id] ?? []}
                                onChange={(e, value) => {
                                    if (value && (value as string[]).length > 0) {
                                        setActionFilter({
                                            ...actionFilter,
                                            [config.id]: value as string[],
                                        })
                                    } else {
                                        setActionFilter(omit([config.id], actionFilter))
                                    }
                                }}
                                label={config.configName}
                                placeholder={`Select ${config.configName}`}
                            />
                        ))}
                    </YStack>
                ) : (
                    <Loading />
                )}
            </Dialog.Description>
            <XStack
                gap="$2"
                justifyContent="flex-end"
                marginTop="$4"
            >
                <Button
                    onPress={() => {
                        props.setActionFilters(actionFilter)
                        close()
                    }}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    onPress={close}
                >
                    Cancel
                </Button>
                <Button
                    theme="red"
                    variant="outlined"
                    onPress={() => {
                        props.setActionFilters({})
                        close()
                    }}
                >
                    <ClearRounded />
                    Clear
                </Button>
            </XStack>
        </Dialog.Content>
    )
}

// Status filter as a row of toggle pills (design): each status badge toggles on/off,
// selected = full opacity on a raised chip, deselected = dimmed. All start selected.
const StatusFilter = ({
    statusFilter,
    setStatusFilter,
}: {
    statusFilter: PurchaserStatus[]
    setStatusFilter: React.Dispatch<React.SetStateAction<PurchaserStatus[]>>
}) => {
    const toggle = (s: PurchaserStatus) =>
        setStatusFilter(
            statusFilter.includes(s) ? statusFilter.filter(x => x !== s) : [...statusFilter, s],
        )

    return (
        <XStack
            flexWrap="wrap"
            alignItems="center"
            rowGap="$1"
        >
            {PurchaserStatuses.map(s => {
                const on = statusFilter.includes(s)
                return (
                    <View
                        key={s}
                        cursor="pointer"
                        onPress={() => toggle(s)}
                        paddingHorizontal={6}
                        height={28}
                        justifyContent="center"
                        borderRadius="$3"
                        animation="quick"
                        opacity={on ? 1 : 0.4}
                        hoverStyle={{ opacity: on ? 1 : 0.7 }}
                    >
                        <StatusBadge status={s} />
                    </View>
                )
            })}
        </XStack>
    )
}

const AllFilersModal = forwardRef(
    (
        {
            statusFilter,
            setStatusFilter,
            agentsFilter,
            setAgentsFilter,
            actionFilters,
            setActionFilters,
            onClose,
        }: any,
        ref: any,
    ) => {
        const purchaserConfigApi = usePurchaserConfigApi()

        return (
            <Dialog.Content
                ref={ref}
                bordered
                elevate
                padding="$4"
                borderRadius="$4"
            >
                <Dialog.Close asChild>
                    <Button
                        size="$3"
                        circular
                        position="absolute"
                        top="$2"
                        right="$2"
                        onPress={onClose}
                    >
                        <CloseRounded />
                    </Button>
                </Dialog.Close>
                <Dialog.Title>All Filters</Dialog.Title>
                <Dialog.Description>
                    <YStack gap="$4">
                        <StatusFilter {...{ statusFilter, setStatusFilter }} />
                        <YStack>
                            <Label>Agents</Label>
                            <MultipleAgentInput
                                value={agentsFilter}
                                onChange={setAgentsFilter}
                                filter={x => x.salesAgent}
                            />
                        </YStack>

                        {purchaserConfigApi.data ? (
                            <>
                                {purchaserConfigApi.data.map(config => (
                                    <Autocomplete
                                        key={config.id}
                                        multiple
                                        options={config.configValues.map(v => v.value)}
                                        value={actionFilters[config.id] ?? []}
                                        onChange={(e, value) => {
                                            if (value && (value as string[]).length > 0) {
                                                setActionFilters({
                                                    ...actionFilters,
                                                    [config.id]: value as string[],
                                                })
                                            } else {
                                                setActionFilters(omit([config.id], actionFilters))
                                            }
                                        }}
                                        label={config.configName}
                                        placeholder={`Select ${config.configName}`}
                                    />
                                ))}
                            </>
                        ) : (
                            <Loading />
                        )}
                    </YStack>
                </Dialog.Description>
                <XStack
                    gap="$2"
                    justifyContent="flex-end"
                    marginTop="$4"
                >
                    <Button
                        variant="outlined"
                        onPress={onClose}
                    >
                        Close
                    </Button>
                </XStack>
            </Dialog.Content>
        )
    },
)

export default function PurchasersTable() {
    const trpc = useTRPC()
    const updateSavedFilterMutation = useMutation(trpc.savedFilters.updateSavedFilter.mutationOptions())
    const purchasersApi = usePurchasersApi()
    const savedFiltersApi = useSavedFiltersApi()
    const navigate = useNavigate()
    const orgId = useOrgId()
    const { user } = useUserData()
    const usersApi = useUsersApi()

    // Calculate default agent filter based on user role
    const defaultAgentFilter = user.salesAgent
        ? [user.userId]
        : user.salesAssistantLeadAgentId
          ? [user.salesAssistantLeadAgentId]
          : []

    const [statusFilter, setStatusFilter] = useSessionStorageState<PurchaserStatus[]>(
        'purchasers_statusFilter',
        (purchasersApi.params.status ?? PurchaserStatuses.filter(s => s !== 'lost')) as PurchaserStatus[],
    )
    const [agentsFilter, setAgentsFilter] = useSessionStorageState<string[]>(
        'purchasers_agentsFilter',
        purchasersApi.params.agentIds ?? defaultAgentFilter,
    )
    const [actionFilters, setActionFilters] = useSessionStorageState<Record<string, string[]>>(
        'purchasers_actionFilters',
        purchasersApi.params.actionFilters ?? {},
    )

    useEffect(() => {
        if (usersApi.data) {
            if (user.salesAssistantConjunctionalAgencyId) {
                setAgentsFilter(
                    usersApi.data
                        .filter(
                            x =>
                                x.conjunctionalAgencyId == user.salesAssistantConjunctionalAgencyId &&
                                user.salesAgent,
                        )
                        .map(prop('userId')),
                )
            }
        }
    }, [usersApi.data != undefined])

    const [search, setSearch] = useSessionStorageState<string>(
        'purchasers_search',
        purchasersApi.params.search ?? '',
    )
    const [actionFilterOpen, setActionFilterOpen] = useState(false)
    const [saveFilterOpen, setSaveFilterOpen] = useState(false)
    const [newFilterName, setNewFilterName] = useState('')
    const [updateFilterLoading, setUpdateFilterLoading] = useState<string>()

    const [activeFilter, setActiveFilter] = useState<string>()
    const [allFiltersOpen, setAllFilersOpen] = useState(false)

    const savedFilters = (savedFiltersApi.data as any)?.purchaser ?? ({} as Record<string, SavedFilterSchema>)

    const saveFilter = async (name: string) =>
        updateSavedFilterMutation.mutateAsync({
            purchaser: {
                ...savedFilters,
                [name]: {
                    status: statusFilter,
                    agentIds: agentsFilter,
                    search,
                    actions: actionFilters,
                },
            },
        })

    const [tabValue, setTabValue] = useSearchParamState<'list'>('view', 'list')

    const applyParamsMemo = useMemo(
        () => [statusFilter, agentsFilter, search, actionFilters],
        [statusFilter, agentsFilter, search, actionFilters],
    )

    const [debouncedApplyParams] = useDebounce(applyParamsMemo, 100)

    useEffect(() => {
        const searchParams: Partial<PurchaserListFilters> = {
            search: search,
            status: statusFilter,
            agentIds: agentsFilter,
            actionFilters,
        }

        purchasersApi.setParams(x => ({
            ...x,
            ...searchParams,
        }))
    }, debouncedApplyParams)

    return (
        <>
            <View
                paddingHorizontal="$4"
                $sm={{ paddingHorizontal: '$6' }}
                display="flex"
                flexDirection="column"
                height="100%"
                data-tour="clients-section"
            >
                <SectionHead
                    title="Clients"
                    subtitle={(() => {
                        const count = purchasersApi.found ?? purchasersApi.data?.length ?? 0
                        return `${count} ${count === 1 ? 'client' : 'clients'}`
                    })()}
                    buttons={
                        <XStack gap="$2">
                            <Button
                                variant="outlined"
                                fontWeight="600"
                                borderRadius="$4"
                                onPress={() => window.open(`/${orgId}/wishlist`, '_blank')}
                                data-tour="view-wishlist-button"
                                data-help-id="clients-view-wishlist"
                            >
                                <ExternalLink size={16} />
                                View Wishlist Page
                            </Button>
                            {(user.permissions.includes('purchasers.edit') ||
                                user.permissions.includes('purchasers.fullControl')) && (
                                <Button
                                    theme="primary"
                                    fontWeight="600"
                                    borderRadius="$4"
                                    onPress={() => navigate('new')}
                                    data-help-id="clients-add-button"
                                >
                                    <AddRounded color="white" />
                                    Add Client
                                </Button>
                            )}
                        </XStack>
                    }
                />
                <Card
                    marginTop="$4"
                    marginBottom="$3"
                    padding="$3"
                    gap="$3"
                >
                    <XStack gap="$4" width="100%">
                        <View flex={1} minWidth={0} data-help-id="clients-search">
                            <Input
                                width="100%"
                                placeholder="Search by client, agent, or status"
                                value={search}
                                onChangeText={setSearch}
                            />
                        </View>
                        <View $sm={{ display: 'none' }}>
                            <View position="relative">
                                <Button
                                    size="$3"
                                    circular
                                    onPress={() => setAllFilersOpen(true)}
                                    data-help-id="clients-filter-button"
                                >
                                    <FilterAltRounded />
                                </Button>
                                {[
                                    statusFilter.length,
                                    agentsFilter.length,
                                    search.length,
                                    ...Object.values(actionFilters).map(x => x.length),
                                ].filter(x => x > 0).length > 0 && (
                                    <View
                                        position="absolute"
                                        top={-4}
                                        right={-4}
                                        backgroundColor="$red10"
                                        borderRadius={999}
                                        width={16}
                                        height={16}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Text
                                            color="white"
                                            fontSize={10}
                                        >
                                            {
                                                [
                                                    statusFilter.length,
                                                    agentsFilter.length,
                                                    search.length,
                                                    ...Object.values(actionFilters).map(x => x.length),
                                                ].filter(x => x > 0).length
                                            }
                                        </Text>
                                    </View>
                                )}
                            </View>
                        </View>
                    </XStack>
                    <View
                        display="none"
                        $sm={{ display: 'flex' }}
                        data-help-id="clients-filter-status"
                    >
                        <StatusFilter
                            {...{
                                statusFilter,
                                setStatusFilter,
                            }}
                        />
                    </View>
                </Card>
                <View marginTop="$4" flex={1} minHeight={0}>
                    <View
                        display={tabValue != 'list' ? 'none' : undefined}
                        height="100%"
                    >
                        <PurchasersTableDesktop purchasersApi={purchasersApi} />
                        <PurchasersTableMobile purchasersApi={purchasersApi} />
                    </View>
                </View>
            </View>
            <Dialog
                open={actionFilterOpen}
                onOpenChange={setActionFilterOpen}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        backgroundColor="$overlay"
                    />
                    <ActionFilterModal
                        actionFilters={actionFilters}
                        close={() => setActionFilterOpen(false)}
                        setActionFilters={setActionFilters}
                    />
                </Dialog.Portal>
            </Dialog>
            <Dialog
                open={saveFilterOpen}
                onOpenChange={setSaveFilterOpen}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        backgroundColor="$overlay"
                    />
                    <Dialog.Content
                        bordered
                        elevate
                        padding="$4"
                        borderRadius="$4"
                    >
                        <Dialog.Close asChild>
                            <Button
                                size="$3"
                                circular
                                position="absolute"
                                top="$2"
                                right="$2"
                                onPress={() => setSaveFilterOpen(false)}
                            >
                                <CloseRounded />
                            </Button>
                        </Dialog.Close>
                        <Dialog.Title>Save Filter</Dialog.Title>
                        <Dialog.Description>
                            <Input
                                height={44}
                                value={newFilterName}
                                placeholder="Filter Name"
                                onChangeText={text => setNewFilterName(text)}
                                width="100%"
                            />
                        </Dialog.Description>
                        <XStack
                            gap="$2"
                            justifyContent="flex-end"
                            marginTop="$4"
                        >
                            <Button
                                onPress={async () => {
                                    await saveFilter(newFilterName)
                                    await savedFiltersApi.refresh()
                                    setActiveFilter(newFilterName)
                                    setSaveFilterOpen(false)
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                variant="outlined"
                                onPress={() => setSaveFilterOpen(false)}
                            >
                                Cancel
                            </Button>
                        </XStack>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
            <Dialog
                open={allFiltersOpen}
                onOpenChange={setAllFilersOpen}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        backgroundColor="$overlay"
                    />
                    <AllFilersModal
                        {...{
                            statusFilter,
                            setStatusFilter,
                            agentsFilter,
                            setAgentsFilter,
                            actionFilters,
                            setActionFilters,
                        }}
                        onClose={() => setAllFilersOpen(false)}
                    />
                </Dialog.Portal>
            </Dialog>
        </>
    )
}
